// src/encounter/lib/add-line-helpers.ts
//
// What the Diagnosis / Order add lines (ap-add-line.tsx) and the backslash line (ap-slash-add-line.tsx) share: the debounce hook,
// the Retry hook, the row and status helpers, and the order types the Orders line searches. Moved here unchanged from
// ap-add-line.tsx so both lines read ONE copy (a component file may not export functions: react-refresh).
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { OrderType as DsOrderType } from '@scripsteam/scrips-signal-ds';
import type { AddLineEntry, AddLinePick, AddLineStatus } from '../ap-add-line';
import type { AddLineRow, AddLineSection } from './add-line-sections';

/** The catalogue searches answer from 2 characters (the hooks' own minimum). */
export const MIN_CATALOGUE_CHARS = 2;
export const ADD_LINE_ERROR = "Couldn't search.";

export type Row = AddLineRow<AddLineEntry<AddLinePick>>;
export const row = (entry: AddLineEntry<AddLinePick>): Row => ({ key: entry.key, term: entry.term, meta: entry.hint, value: entry });
export const flat = (sections: AddLineSection<AddLineEntry<AddLinePick>>[]): AddLineEntry<AddLinePick>[] =>
  sections.flatMap((s) => s.rows.map((r) => r.value));
export const hintOf = (code: string | undefined, source: string): string => [code?.trim(), source].filter(Boolean).join(' · ');

export const norm = (code: string | null | undefined) => (code ?? '').trim().toUpperCase();

/** The typed text, settled for `ms` (the catalogue searches read this, the list's open state does not). */
export function useSettled(value: string, ms: number): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), value.trim() ? ms : 0);
    return () => clearTimeout(t);
  }, [value, ms]);
  return value.trim() ? settled : value;
}

/**
 * Retry: run again the searches of THIS line that failed for THIS text, and nothing else in the shared
 * client (Codex P2, 6 Oct, two passes). `isOurs` names the line's own query keys.
 */
export function useRetryFailed(isOurs: (key: readonly unknown[]) => boolean): () => void {
  const client = useQueryClient();
  return () => void client.refetchQueries({ predicate: (q) => q.state.status === 'error' && isOurs(q.queryKey) });
}

/** "Couldn't search." — or, when other sources answered, the sources that failed by name. */
export const failureText = (failed: readonly string[], rows: number): string =>
  failed.length > 0 && rows > 0 ? `Couldn't search ${failed.join(', ')}.` : ADD_LINE_ERROR;

/** What the live region says for each kind of pick: only a diagnosis or an article is ADDED by the pick. */
export function announcePick(pick: AddLinePick, term: string): string {
  if (pick.kind === 'item' && pick.category !== 'diagnosis') return `Opened the order form for ${term}`;
  if (pick.kind === 'order-set') return `Applying the order set ${term}`;
  if (pick.kind === 'bundle') return `Importing the bundle ${term}`;
  return `Added ${term}`;
}

/** The status under the line, from what the searches report. */
export function lineStatus(o: { typed: boolean; browsing?: boolean; current: boolean; loading: boolean; error: boolean; rows: number; answered: boolean }): AddLineStatus {
  // D-75 (4): the field is open with no text — the list is your favourites, or "No favourites yet".
  if (!o.typed && o.browsing) {
    if (o.error) return 'error';
    if (o.loading) return 'searching';
    return o.rows === 0 ? 'empty' : 'idle';
  }
  if (!o.typed) return 'idle';
  if (o.current && o.error) return 'error';
  if (!o.current || o.loading) return 'searching';
  if (o.rows === 0 && o.answered) return 'empty';
  return 'idle';
}


/**
 * Every order type the line can search, in the order the A&P lists them, with its kind word and its
 * catalogue's REAL source (the endpoint behind `useApCategorySearch`). Fixed, so the hook calls are too.
 */
export const ORDER_SEARCH: { type: DsOrderType; group: string; kind: string; catalogue: string }[] = [
  // Master's order search (the clinic's tests and panels, then the Scrips standard list) when it answers;
  // else the legacy lab code list (PROD-2931).
  { type: 'lab', group: 'Labs', kind: 'Lab order', catalogue: 'lab catalogue' },
  // The provider's GetMedication favourites, then the master SearchValueSets2 Medications catalogue (DDC).
  { type: 'medication', group: 'Medications', kind: 'Medication', catalogue: 'drug catalogue' },
  { type: 'imaging', group: 'Imaging', kind: 'Imaging', catalogue: 'radiology catalogue' },
  { type: 'procedure', group: 'Procedures', kind: 'Procedure', catalogue: 'CPT catalogue' },
  // /api/Master/ProcedureCodesList: a referral row names the PROCEDURE, never a practitioner. With the `referrals` flag on
  // the rows are the clinic's specialities instead (PROD-3336), labelled below.
  { type: 'referral', group: 'Referrals', kind: 'Referral', catalogue: 'procedure catalogue' },
  { type: 'vaccination', group: 'Vaccinations', kind: 'Vaccination', catalogue: 'vaccine catalogue' },
];
